import { defineNuxtRouteMiddleware, showError, useError } from "nuxt/app";
import { useBugReporter, useDirectBackend, usePinia } from "#imports";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import type { SalesChannel } from "~/domain/sales-channels";
import { useBusinessStore } from "~/stores/business";
import { useCustomerStore } from "~/stores/customer";
import { useDiscountsStore } from "~/stores/discounts";
import { useGiftCardStore } from "~/stores/giftCard";
import { useLoyaltyProgramStore } from "~/stores/loyaltyProgram";
import { useOutletStore } from "~/stores/outlet";

const abortWithUnexpectedError = (
  message: string = "We could not load the location' settings. Please try again in a few minutes.",
) => {
  throw showError({
    statusCode: 500,
    statusMessage: message,
  });
};

export default defineNuxtRouteMiddleware(async (to) => {
  if (useError().value) return;

  const pinia = usePinia();
  const business = useBusinessStore(pinia);
  const outlet = useOutletStore(pinia);
  const loyaltyProgram = useLoyaltyProgramStore(pinia);
  const giftCard = useGiftCardStore(pinia);
  const customer = useCustomerStore(pinia);
  const discounts = useDiscountsStore(pinia);

  const { outletRepo } = useDirectBackend();

  if (!outlet.isLoaded) {
    return abortWithUnexpectedError(
      "EnsureOutletMetadataIsLoadedMiddleware must run after outlet has been loaded only.",
    );
  }

  const routeOrderingMode = (
    Array.isArray(to.params.orderingMode)
      ? to.params.orderingMode[0]
      : to.params.orderingMode
  ) as IDomainOrderingMode;

  outlet.patch({
    activeOrderingMode: routeOrderingMode,
  });

  if (
    outlet.orderingModes.delivery.menus.length > 0 ||
    outlet.orderingModes.pickup.menus.length > 0
  ) {
    return;
  }

  if (!routeOrderingMode) {
    return abortWithUnexpectedError(
      "EnsureOutletMetadataIsLoadedMiddleware requires a route with orderingMode parameter.",
    );
  }

  const orderingModes: IDomainOrderingMode[] = [];
  if (outlet.hasDelivery) orderingModes.push("delivery");
  if (outlet.hasPickup) orderingModes.push("pickup");

  if (!orderingModes.length) {
    return;
  }

  try {
    const fetchMenus = async (
      businessId: string,
      outletId: string,
      orderingMode: IDomainOrderingMode,
      salesChannel: SalesChannel,
    ) => {
      const res = await outletRepo.getMenus({
        outletId,
        businessId,
        salesChannel,
        orderingMode,
      });
      return res.data;
    };

    const fetchMetadataForModes = async (
      businessId: string,
      outletId: string,
      orderingMode: IDomainOrderingMode[],
      salesChannel: SalesChannel,
    ) => {
      const settings = await outletRepo.getSettings({
        outletId,
        salesChannel,
        orderingMode,
      });

      if (!settings) {
        throw new Error("Some of the required metadata could not be fetched.");
      }

      const menus = await Promise.all(
        settings.map((s) =>
          fetchMenus(businessId, outletId, s.mode, salesChannel),
        ),
      );

      return settings.map((s, index) => ({
        mode: s.mode,
        isEnabled: s.isEnabled,
        preparationMinutes: s.preparationMinutes,
        estimatedCompletionMinutes: s.estimatedCompletionMinutes,
        acceptsPackaging: s.acceptsPackaging,
        acceptsAsapOrders: s.acceptsAsapOrders,
        acceptsPreOrders: s.acceptsPreOrders,
        slotLength: s.slotLength,
        openingHours: s.openingHours,
        outletState: s.operationalState,
        paymentMethods: s.paymentMethods,
        menus: menus[index].map((m) => ({
          id: m.id,
          name: m.name,
          slug: m.slug,
          icon: m.icon ?? "",
          summary: m.summary,
        })),
      }));
    };

    const [settings] = await Promise.all([
      fetchMetadataForModes(
        business.id,
        outlet.id,
        orderingModes,
        business.salesChannel,
      ),
      loyaltyProgram.load({
        businessId: business.id,
        outletId: outlet.id,
        salesChannel: business.salesChannel,
      }),
      giftCard.loadProviders(outlet.id, business.salesChannel),
      customer.syncLoyalty(business.id, business.salesChannel, outlet.id),
      customer.trackGoogleConversion(outlet.id, business.salesChannel),
      discounts.load(business.id, outlet.id, routeOrderingMode),
    ]);

    outlet.patch({
      orderingModes: {
        delivery:
          settings.find((s) => s.mode === "delivery") ??
          outlet.orderingModes.delivery,
        pickup:
          settings.find((s) => s.mode === "pickup") ??
          outlet.orderingModes.pickup,
      },
    });
  } catch (error: unknown) {
    useBugReporter().report(error, {
      tags: {
        "code.flag": "middleware-outlet-metadata",
      },
    });

    return abortWithUnexpectedError();
  }
});
